import * as Sentry from '@sentry/react';
import { api, scraping } from 'libs';
import { runtimeConfig } from './runtime-config';

declare global {
  interface Window {
    CSRF_TOKEN: string;
    Intercom?: (...args: unknown[]) => void;
    intercomSettings?: Record<string, unknown>;
  }
}
const versionHash = process.env.VERSION_HASH || '';

const getMetaContent = (name: string) => {
  const meta = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;

  if (!meta?.content) {
    throw new Error(`Missing required meta tag: ${name}`);
  }

  return meta.content;
};

/**
 * Configure Sentry
 */
if (runtimeConfig.sentryClientDsn) {
  Sentry.init({
    dsn: runtimeConfig.sentryClientDsn,
    environment: runtimeConfig.appEnv,
    release: versionHash,
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this.
      // See http://stackoverflow.com/questions/4113268
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      // Network errors such as going offline or being blocked by a proxy
      'Failed to fetch',
      // Caused by extension: https://stackoverflow.com/questions/50272371/what-is-betterjspop-error-on-chrome
      'Cannot redefine property: BetterJsPop',
    ],
    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Safari extensions
      /^safari-extension:\/\//i,
      // Firefox extensions
      /^resource:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      // Ignore Google flakiness
      /\/(gtm|ga|analytics)\.js/i,
    ],
  });
}

/**
 * Retrieve CSRF token from template meta tag.
 */
const token = getMetaContent('csrf-token');

/**
 * Attach CSRF token to global window object.
 */
window.CSRF_TOKEN = token;

api.configure({
  csrfToken: token,
  versionHash,
});

// Setup scraping detection
scraping.initScrapingDetection();
scraping.registerHeaders();
